import type { SalesFlow } from '@/types/location-features'
import { groupRule, type RuleGroupLike } from '@/lib/menu/includedSelectionsCart'

export type { SalesFlow }

/**
 * ¿Este plato, en este local, se pide con el flujo guiado?
 *
 * Única fuente de la decisión. Antes estaba repartida: una función que parseaba
 * la variable de entorno `NEXT_PUBLIC_GUIDED_FLOW_SLUGS`, más la comprobación de
 * `zoned_build` copiada en CUATRO sitios (KioskShell, RestaurantMicrosite,
 * MenuSection, FeaturedSection).
 *
 * Dos reglas, y el orden no importa porque ambas tienen que cumplirse:
 *
 * 1. El dueño eligió `'guided'` en su panel. La comparación es ESTRICTA a
 *    propósito: `resolveFeatures` hace merge superficial, así que solo cubre la
 *    clave ausente — un valor corrupto en el jsonb llegaría tal cual. Cualquier
 *    cosa que no sea exactamente `'guided'` cae en clásico, y así ningún local
 *    puede entrar en guiado por accidente.
 *
 * 2. El plato NO es de armado por zonas. El flujo guiado no sabe armar pizzas
 *    por mitades y hay 6 platos zonados vivos en producción, todos en
 *    `redy-test` (verificado 2026-08-20). Esos abren siempre el modal legado,
 *    diga lo que diga el ajuste.
 *
 * 3. El plato NO tiene un grupo de selecciones incluidas (el mixto,
 *    lib/menu/includedSelections). El guiado cotiza cada opción por su cuenta y
 *    no sabe que la tercera proteína cuesta y la segunda no: mostraría un total
 *    que la caja no cobra. Mismo trato que la pizza: modal legado, que sí lo sabe.
 */
type GroupRow = RuleGroupLike | null | undefined

export function usesGuidedFlow(
  salesFlow: SalesFlow | string | null | undefined,
  item: {
    zoned_build?: boolean | null
    /** Forma del kiosko y del menú web. */
    item_modifier_groups?: ReadonlyArray<{ modifier_groups?: GroupRow }> | null
    /** Forma del catálogo del POS. */
    modifier_groups?: ReadonlyArray<GroupRow> | null
  }
): boolean {
  if (salesFlow !== 'guided' || item.zoned_build === true) return false
  const groups: GroupRow[] = [
    ...(item.item_modifier_groups ?? []).map((row) => row.modifier_groups),
    ...(item.modifier_groups ?? []),
  ]
  return !groups.some((g) => g != null && groupRule(g) !== null)
}
